import { getFirstPodcast } from "../private/api/getFirstPodcast";
import { getPreviewPodcast } from "../private/api/getPreviewPodcast";
import { getSignedUrl } from "../private/api/getSignedUrl";
import { getPagePodcasts } from "../private/api/getPagePodcasts";
import { getOtherPodcastList } from "../private/api/otherPodcasts";

const Audioplayer = {
  namespaced: true,

  state: {
    list: {},
    otherPodcasts: {},

    bufferedRanges: [],
    audioplayer: null,

    isPlaying: false,
    isLoading: false,
    volume: 1,
    speed: 1,
    currentTime: 0,
    duration: 0,

    activePodcastId: null,
    podcastPreviewText: null,

    isAudioplayerLoading: false,
    isAudioplayerReady: false,
    isStickyPanelOpened: false,
    isLoadingPodcastsList: false,

    currentAudioRequest: null,
    wasTimeRestoredFromStorage: false,
    
    progressUpdateInterval: null,
  },

  actions: {
    async initPlayer({ commit, dispatch, state }) {
      await dispatch('initFirstPodcast');
      await commit('SET_AUDIOPLAYER_API');

      // Инициализация Media Session для мобильных устройств
      if ('mediaSession' in navigator) {
        await dispatch('setupMediaSession');
      }

      const savedVolume = localStorage.getItem('audio_volume');
      commit('SET_VOLUME', savedVolume !== null ? parseFloat(savedVolume) : 0.5);

      const savedSpeed = localStorage.getItem('audio_speed');
      commit('SET_SPEED', savedSpeed !== null ? parseFloat(savedSpeed) : 1);

      commit('SET_READY_AUDIOPLAYER_FLAG');
      
      // Настройка обработчика видимости страницы
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && state.isPlaying) {
          // Обновляем метаданные когда страница скрыта
          dispatch('updateMediaSessionMetadata');
        }
      });
    },

    async setupMediaSession({ commit, dispatch, state }) {
      try {
        // Настройка обработчиков медиакнопок
        navigator.mediaSession.setActionHandler('play', () => {
          console.log('Media Session: Play button pressed');
          return dispatch('resumeAudio');
        });
        
        navigator.mediaSession.setActionHandler('pause', () => {
          console.log('Media Session: Pause button pressed');
          return dispatch('pauseAudio');
        });
        
        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
          console.log('Media Session: Seek backward');
          const skipTime = details.seekOffset || 10;
          const newTime = Math.max(0, state.currentTime - skipTime);
          return dispatch('resumeAudio', newTime);
        });
        
        navigator.mediaSession.setActionHandler('seekforward', (details) => {
          console.log('Media Session: Seek forward');
          const skipTime = details.seekOffset || 10;
          const newTime = Math.min(state.duration || 0, state.currentTime + skipTime);
          return dispatch('resumeAudio', newTime);
        });
        
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          console.log('Media Session: Seek to', details.seekTime);
          if (details.fastSeek && state.audioplayer && 'fastSeek' in state.audioplayer) {
            state.audioplayer.fastSeek(details.seekTime);
          } else {
            return dispatch('resumeAudio', details.seekTime);
          }
          commit('SET_CURRENT_TIME', details.seekTime);
        });
        
        // Предотвращение стандартного поведения (для iOS)
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
      } catch (error) {
        console.warn('Media Session setup error:', error);
      }
    },

    async updateMediaSessionMetadata({ state, commit }) {
      if (!('mediaSession' in navigator)) return;
      
      const currentPodcast = state.list[state.activePodcastId];
      if (!currentPodcast) return;

      try {
        // Обновление метаданных для уведомления на мобильных устройствах
        const metadata = {
          title: currentPodcast.title || 'Подкаст',
          artist: currentPodcast.author || 'Автор неизвестен',
          album: currentPodcast.album || '',
        };

        // Добавляем обложку если есть
        if (currentPodcast.image_url) {
          metadata.artwork = [
            { src: currentPodcast.image_url, sizes: '96x96', type: 'image/png' },
            { src: currentPodcast.image_url, sizes: '128x128', type: 'image/png' },
            { src: currentPodcast.image_url, sizes: '192x192', type: 'image/png' },
            { src: currentPodcast.image_url, sizes: '256x256', type: 'image/png' },
            { src: currentPodcast.image_url, sizes: '384x384', type: 'image/png' },
            { src: currentPodcast.image_url, sizes: '512x512', type: 'image/png' },
          ];
        }

        navigator.mediaSession.metadata = new MediaMetadata(metadata);

        // Обновление состояния воспроизведения
        navigator.mediaSession.playbackState = state.isPlaying ? 'playing' : 'paused';
        
        // Установка позиции воспроизведения (для iOS)
        if ('setPositionState' in navigator.mediaSession && state.duration > 0) {
          navigator.mediaSession.setPositionState({
            duration: state.duration,
            playbackRate: state.speed,
            position: state.currentTime
          });
        }
      } catch (error) {
        console.warn('Failed to update Media Session metadata:', error);
      }
    },

    startProgressSync({ state, commit }) {
      // Очистка предыдущего интервала
      if (state.progressUpdateInterval) {
        clearInterval(state.progressUpdateInterval);
        state.progressUpdateInterval = null;
      }
      
      // Запуск синхронизации прогресса с Media Session
      state.progressUpdateInterval = setInterval(() => {
        if (state.isPlaying && state.duration > 0 && state.audioplayer) {
          // Сохранение прогресса
          if (state.activePodcastId) {
            localStorage.setItem('last_audio_id', state.activePodcastId);
            localStorage.setItem('last_remember_time', state.currentTime);
          }
        }
      }, 1000);
    },

    stopProgressSync({ state }) {
      if (state.progressUpdateInterval) {
        clearInterval(state.progressUpdateInterval);
        state.progressUpdateInterval = null;
      }
    },

    async initFirstPodcast({ commit, dispatch }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      try {
        const data = await getFirstPodcast();
        const podcast = data[0].podcast_atributes;
        commit('SET_NEW_AUDIO', podcast);
        commit('WRITE_ID_ACTIVE_PODCAST', podcast.id);
        
        // Обновление метаданных для медиасессии
        await dispatch('updateMediaSessionMetadata');
      } catch (error) {
        console.error('Failed to init first podcast:', error);
      } finally {
        commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
      }
    },

    async initPreviewPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      try {
        const data = await getPreviewPodcast();
        commit('SET_PREVIEW', data.title);
      } catch (error) {
        console.error('Failed to init preview podcast:', error);
      } finally {
        commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
      }
    },

    async activeCurrentPodcats({ commit, dispatch, state }) {
      commit('TOGGLE_STICKY_PANEL', true);
      commit('SET_LOADING_AUDIO_FLAG', true);
      try {
        const currentPodcast = state.list[state.activePodcastId];
        if (!currentPodcast) {
          throw new Error('Current podcast not found');
        }

        if (!currentPodcast.signed_url) {
          await dispatch('useSignedUrlForAudio');
        }

        if (state.audioplayer.src !== currentPodcast.signed_url) {
          commit('SET_AUDIO_URL', currentPodcast.signed_url);

          await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('Timeout loading audio')), 10000);
            const onCanPlay = () => {
              clearTimeout(timer);
              state.audioplayer.removeEventListener('canplay', onCanPlay);
              resolve();
            };
            state.audioplayer.addEventListener('canplay', onCanPlay);
            if (state.audioplayer.readyState > 0) onCanPlay();
          });

          // Получение длительности аудио
          commit('SET_DURATION', state.audioplayer.duration);

          if (
            !state.wasTimeRestoredFromStorage &&
            localStorage.getItem('last_audio_id') === String(state.activePodcastId)
          ) {
            const savedTime = parseFloat(localStorage.getItem('last_remember_time') || '0');
            if (!isNaN(savedTime)) {
              commit('SET_CURRENT_TIME', savedTime);
              state.audioplayer.currentTime = savedTime;
            }
            state.wasTimeRestoredFromStorage = true;
          } else {
            state.audioplayer.currentTime = state.currentTime;
          }
        }

        commit('SET_VOLUME', state.volume);
        commit('SET_SPEED', state.speed);
        commit('SET_CURRENT_TIME', state.currentTime);

        state.audioplayer.currentTime = state.currentTime;
        await state.audioplayer.play();
        commit('SET_PLAYING_FLAG', true);
        
        // Обновление медиасессии
        await dispatch('updateMediaSessionMetadata');
        dispatch('startProgressSync');
      } catch (error) {
        console.error('Playback failed:', error);
        commit('SET_PLAYING_FLAG', false);
        throw error;
      } finally {
        commit('SET_LOADING_AUDIO_FLAG', false);
      }
    },

    async useSignedUrlForAudio({ state, commit }) {
      if (state.currentAudioRequest) {
        state.currentAudioRequest.abort?.();
      }

      const currentPodcast = state.list[state.activePodcastId];
      if (!currentPodcast || !currentPodcast.audio_file) return;

      const shouldRefreshLink = () => {
        if (!currentPodcast.signed_url) return true;
        if (!currentPodcast.signed_expires_at) return true;
        const expiresAt = new Date(currentPodcast.signed_expires_at);
        const refreshThreshold = new Date(expiresAt.getTime() - 600000);
        return new Date() > refreshThreshold;
      };

      if (!shouldRefreshLink()) return;

      const match = currentPodcast.audio_file.match(/\/([^\/?#]+)$/);
      const name = match ? match[1] : currentPodcast.audio_file;
      const request = getSignedUrl(name);
      commit('SET_AUDIO_REQUEST', request);

      try {
        const data = await request;
        const cleanSignedUrl = data.signed_url.replace(/\.sdn/, '');
        commit('UPDATE_PODCAST_SIGNED_URL', {
          id: state.activePodcastId,
          signed_url: cleanSignedUrl,
          expires_at: data.expires_at
        });
      } catch (error) {
        console.error('Failed to get signed URL:', error);
        throw error;
      }
    },

    async changeVolume({ commit }, volume) {
      commit('SET_VOLUME', volume);
      localStorage.setItem('audio_volume', volume);
    },

    async changeSpeed({ commit }, speed) {
      commit('SET_SPEED', speed);
      localStorage.setItem('audio_speed', speed);
    },

    async closeStickyPanel({ state, commit, dispatch }, status) {
      commit('TOGGLE_STICKY_PANEL', status);
      if (state.audioplayer) {
        await state.audioplayer.pause();
      }
      commit('SET_PLAYING_FLAG', false);
      dispatch('stopProgressSync');
    },

    async playAudio({ state, commit, dispatch }, { id }) {
      if (state.isPlaying) {
        await dispatch('pauseAudio');
      }
      commit('SET_CURRENT_TIME', 0);
      commit('WRITE_ID_ACTIVE_PODCAST', id);
      state.wasTimeRestoredFromStorage = false;
      
      // Остановка синхронизации прогресса перед переключением трека
      dispatch('stopProgressSync');
      
      await dispatch('activeCurrentPodcats');
    },

    async pauseAudio({ state, commit, dispatch }) {
      if (!state.audioplayer || !state.isPlaying) return false;
      await state.audioplayer.pause();
      commit('SET_PLAYING_FLAG', false);
      
      // Обновление медиасессии
      dispatch('updateMediaSessionMetadata');
      dispatch('stopProgressSync');
    },

    async resumeAudio({ state, commit, dispatch }, seekTime) {
      if (!state.audioplayer) throw new Error('Аудиоплеер не инициализирован');
      
      if (typeof seekTime === 'number' && !isNaN(seekTime)) {
        const validatedTime = Math.max(0, seekTime);
        commit('SET_CURRENT_TIME', validatedTime);
        state.audioplayer.currentTime = validatedTime;
      }
      
      if (state.audioplayer.paused) {
        await dispatch('activeCurrentPodcats');
      }
    },

    async fowardToPlace({ state, dispatch }, { time, idPodcast }) {
      if (idPodcast !== state.activePodcastId) {
        // Обновляем ID активного подкаста
        state.activePodcastId = idPodcast;
        state.wasTimeRestoredFromStorage = false;
        
        // Проверяем есть ли подкаст в списке
        if (!state.list[idPodcast]) {
          console.error('Podcast not found in list');
          return;
        }
        
        dispatch('stopProgressSync');
        await dispatch('activeCurrentPodcats');
        if (state.audioplayer) {
          state.audioplayer.currentTime = time;
        }
      } else {
        if (state.audioplayer) {
          state.audioplayer.currentTime = time;
          commit('SET_CURRENT_TIME', time);
        }
        if (!state.isPlaying && state.audioplayer) {
          await dispatch('resumeAudio', time);
        }
      }
    },

    async addPodcastToList({ commit }, podcast) {
      commit('SET_NEW_AUDIO', podcast);
    },

    async toGetNewPodcastsData({ commit }, exclude) {
      try {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', true);
        commit('CLEAR_OTHER_LIST');
        const data = await getOtherPodcastList(exclude);
        data.results.forEach(item => {
          commit('SET_OTHER_LIST', {
            id: item.id,
            object_id: item.object_id,
            audio_length: item.audio_length,
            comments_count: item.comments_count,
            title: item.title,
            date: item.date,
          });
        });
      } catch (error) {
        console.error(`Ошибка при получении подкастов: ${error}`);
      } finally {
        commit('SET_LOADING_PODCASTS_LIST_STATUS', false);
      }
    },
  },

  mutations: {
    SET_AUDIOPLAYER_API(state) {
      if (state.audioplayer) {
        // Очищаем предыдущий аудиоплеер если он существует
        state.audioplayer.pause();
        state.audioplayer.src = '';
        state.audioplayer = null;
      }

      state.audioplayer = new Audio();

      // Установка атрибутов для корректной работы в фоне (особенно для iOS)
      state.audioplayer.setAttribute('playsinline', '');
      state.audioplayer.setAttribute('webkit-playsinline', '');
      state.audioplayer.crossOrigin = 'anonymous';

      state.audioplayer.addEventListener('timeupdate', () => {
        if (!state.audioplayer) return;

        state.currentTime = state.audioplayer.currentTime;
        
        // Обновление длительности, если она еще не установлена
        if (state.duration === 0 && state.audioplayer.duration > 0) {
          state.duration = state.audioplayer.duration;
        }
      });

      state.audioplayer.addEventListener('durationchange', () => {
        if (state.audioplayer && state.audioplayer.duration > 0) {
          state.duration = state.audioplayer.duration;
        }
      });

      state.audioplayer.addEventListener('loadstart', () => {
        state.isLoading = true;
      });

      state.audioplayer.addEventListener('canplay', () => {
        state.isLoading = false;
      });

      state.audioplayer.addEventListener('progress', () => {
        if (!state.audioplayer) return;
        
        const buffered = state.audioplayer.buffered;
        const ranges = [];

        for (let i = 0; i < buffered.length; i++) {
          ranges.push({
            start: buffered.start(i),
            end: buffered.end(i),
          });
        }

        state.bufferedRanges = ranges;
      });

      // Обработка окончания воспроизведения
      state.audioplayer.addEventListener('ended', () => {
        state.isPlaying = false;
        state.currentTime = 0;
        
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'none';
        }
        
        // Остановка синхронизации прогресса
        if (state.progressUpdateInterval) {
          clearInterval(state.progressUpdateInterval);
          state.progressUpdateInterval = null;
        }
      });

      // Обработка ошибок воспроизведения
      state.audioplayer.addEventListener('error', (e) => {
        console.error('Audio player error:', e);
        state.isLoading = false;
        state.isPlaying = false;
        
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'none';
        }
      });
    },

    UPDATE_PODCAST_SIGNED_URL(state, { id, signed_url, expires_at }) {
      if (state.list[id]) {
        state.list[id].signed_url = signed_url;
        state.list[id].signed_expires_at = expires_at;
      }
    },

    SET_VOLUME(state, volume) {
      const validatedVolume = Number(volume);
      state.volume = isNaN(validatedVolume) || validatedVolume < 0 || validatedVolume > 1 ? 0.5 : validatedVolume;
      if (state.audioplayer) state.audioplayer.volume = state.volume;
    },

    SET_SPEED(state, speed) {
      const validatedSpeed = Number(speed);
      state.speed = isNaN(validatedSpeed) || validatedSpeed <= 0 || validatedSpeed > 4 ? 1 : validatedSpeed;
      if (state.audioplayer) state.audioplayer.playbackRate = state.speed;
    },

    SET_AUDIO_URL(state, url) {
      if (state.audioplayer) {
        state.audioplayer.src = url;
      }
    },

    SET_DURATION(state, duration) {
      state.duration = duration || 0;
    },

    TOGGLE_STICKY_PANEL(state, status) {
      state.isStickyPanelOpened = status;
    },

    WRITE_ID_ACTIVE_PODCAST(state, id) {
      state.activePodcastId = id;
    },

    SET_NEW_AUDIO(state, podcast) {
      state.list[podcast.id] = podcast;
    },

    SET_READY_AUDIOPLAYER_FLAG(state) {
      state.isAudioplayerReady = true;
    },

    SET_PREVIEW(state, text) {
      state.podcastPreviewText = text;
    },

    SET_LOADING_AUDIOPLAYER_FLAG(state, status) {
      state.isAudioplayerLoading = status;
    },

    SET_LOADING_AUDIO_FLAG(state, status) {
      state.isLoading = status;
    },

    SET_PLAYING_FLAG(state, status) {
      state.isPlaying = status;
    },

    SET_AUDIO_REQUEST(state, request) {
      state.currentAudioRequest = request;
    },

    SET_CURRENT_TIME(state, time) {
      state.currentTime = time;
    },

    SET_OTHER_LIST(state, data) {
      state.otherPodcasts[data.id] = data;
    },

    SET_LOADING_PODCASTS_LIST_STATUS(state, status) {
      state.isLoadingPodcastsList = status;
    },

    CLEAR_OTHER_LIST(state){
      state.otherPodcasts = {};
    },
  },

  getters: {
    currentProgress: (state) => {
      if (state.duration > 0) {
        return (state.currentTime / state.duration) * 100;
      }
      return 0;
    },
    
    formattedTime: (state) => {
      const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
      };
      
      return {
        current: formatTime(state.currentTime),
        duration: formatTime(state.duration)
      };
    },
    
    currentPodcast: (state) => {
      return state.list[state.activePodcastId] || null;
    },
  },
};

export default Audioplayer;